import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AlunosFaltantes = ({
  API,
  getAuthHeaders,
  setCurrentView,
  students,
  turmas,
  gerarLinkWhatsApp
}) => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [faltantesData, setFaltantesData] = useState([]);
  const [filteredFaltantes, setFilteredFaltantes] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // 30 dias atrás por padrão
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [selectedTurmaFilter, setSelectedTurmaFilter] = useState('');
  const [minFaltas, setMinFaltas] = useState(''); // Mínimo de faltas para aparecer no relatório

  // Buscar dados de presença por período
  const loadAttendanceRange = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/attendance/range?start=${startDate}&end=${endDate}`, { headers: getAuthHeaders() });
      setAttendanceData(response.data);
      calculateFaltantes(response.data);
    } catch (error) {
      alert('Erro ao carregar dados de presença');
    }
    setLoading(false);
  };

  // Calcular alunos faltantes baseado nos dados de presença
  const calculateFaltantes = (attendanceRecords) => {
    // Calcular total de domingos no período
    const start = new Date(startDate);
    const end = new Date(endDate);
    let totalDomingos = 0;
    const current = new Date(start);

    while (current <= end) {
      if (current.getDay() === 0) { // Domingo
        totalDomingos++;
      }
      current.setDate(current.getDate() + 1);
    }

    // Criar mapa de todos os alunos
    const alunoMap = new Map();

    // Inicializar todos os alunos com dados base
    students.forEach(student => {
      const turma = turmas.find(t => t.id === student.turma_id);
      if (turma) {
        const key = `${student.id}-${student.turma_id}`;
        alunoMap.set(key, {
          student_id: student.id,
          student_name: student.nome_completo,
          turma_id: student.turma_id,
          turma_nome: turma.nome,
          presencas: 0,
          faltas: 0,
          ultimaPresenca: null,
          contato: student.contato
        });
      }
    });

    // Processar registros de presença
    attendanceRecords.forEach(record => {
      const key = `${record.aluno_id}-${record.turma_id}`;
      const aluno = alunoMap.get(key);

      if (aluno) {
        if (record.status === 'presente' || record.status === 'pos_chamada') {
          aluno.presencas++;
          if (!aluno.ultimaPresenca || record.data > aluno.ultimaPresenca) {
            aluno.ultimaPresenca = record.data;
          }
        } else if (record.status === 'ausente') {
          aluno.faltas++;
        }
      }
    });

    // Identificar faltantes
    const faltantesList = [];
    alunoMap.forEach(aluno => {
      // Calcular faltas: se não há registros de presença no período, considera que faltou todos os domingos
      const faltasCalculadas = Math.max(0, totalDomingos - aluno.presencas);

      if (faltasCalculadas >= (minFaltas || 1)) {
        faltantesList.push({
          ...aluno,
          faltas: faltasCalculadas,
          totalDomingos,
          percentualPresenca: totalDomingos > 0 ? ((aluno.presencas / totalDomingos) * 100).toFixed(1) : '0.0',
          diasSemVir: aluno.ultimaPresenca ?
            Math.floor((new Date() - new Date(aluno.ultimaPresenca)) / (1000 * 60 * 60 * 24)) :
            Math.floor((new Date() - new Date(startDate)) / (1000 * 60 * 60 * 24))
        });
      }
    });

    // Ordenar por maior número de faltas
    faltantesList.sort((a, b) => b.faltas - a.faltas);
    setFaltantesData(faltantesList);
    setFilteredFaltantes(faltantesList);
  };

  // Filtrar por turma
  useEffect(() => {
    if (selectedTurmaFilter) {
      setFilteredFaltantes(faltantesData.filter(aluno => aluno.turma_id === selectedTurmaFilter));
    } else {
      setFilteredFaltantes(faltantesData);
    }
  }, [selectedTurmaFilter, faltantesData]);

  // Carregar dados inicial
  useEffect(() => {
    loadAttendanceRange();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-900 text-white px-4 sm:px-6 py-4 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <button
            onClick={() => setCurrentView('alunos')}
            className="self-start px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-200 text-sm font-medium touch-manipulation"
          >
            ← Voltar aos Alunos
          </button>

          <div className="text-center sm:flex-1 sm:mx-6">
            <h1 className="text-xl sm:text-2xl font-bold">📋 Relatório de Alunos Faltantes</h1>
            <p className="text-red-200 text-sm mt-1 hidden sm:block">Acompanhamento - Entre em contato com quem precisa</p>
            <p className="text-red-200 text-sm mt-1 sm:hidden">Acompanhamento</p>
          </div>

          <div className="text-right text-sm hidden sm:block">
            <p>Sistema EBD</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="max-w-7xl mx-auto mt-4 px-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Filtros do Relatório</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Turma</label>
              <select
                value={selectedTurmaFilter}
                onChange={(e) => setSelectedTurmaFilter(e.target.value)}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Todas as turmas</option>
                {turmas.map(turma => (
                  <option key={turma.id} value={turma.id}>{turma.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mín. Faltas</label>
              <input
                type="text"
                placeholder="Digite o número mínimo"
                value={minFaltas}
                onChange={(e) => {
                  const value = e.target.value;
                  // Permitir apenas números
                  if (value === '' || /^\d+$/.test(value)) {
                    setMinFaltas(value === '' ? '' : parseInt(value));
                  }
                }}
                className="w-full px-3 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <button
                onClick={loadAttendanceRange}
                disabled={loading}
                className="w-full px-4 py-3 text-base bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors duration-200 font-medium touch-manipulation"
              >
                {loading ? '🔄 Carregando...' : '📊 Gerar Relatório'}
              </button>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        {filteredFaltantes.length > 0 && (
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📈 Estatísticas do Período</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-red-50 p-4 rounded-lg text-center">
                <div className="text-2xl sm:text-3xl font-bold text-red-600">{filteredFaltantes.length}</div>
                <div className="text-sm sm:text-base text-red-700 mt-1">Alunos Faltantes</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                  {(() => {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    let totalDomingos = 0;
                    const current = new Date(start);
                    while (current <= end) {
                      if (current.getDay() === 0) totalDomingos++;
                      current.setDate(current.getDate() + 1);
                    }
                    return totalDomingos;
                  })()}
                </div>
                <div className="text-sm sm:text-base text-blue-700 mt-1">Domingos no Período</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg text-center sm:col-span-1 col-span-1">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600">
                  {filteredFaltantes.length > 0 ?
                    (filteredFaltantes.reduce((acc, f) => acc + f.faltas, 0) / filteredFaltantes.length).toFixed(1)
                    : '0.0'}
                </div>
                <div className="text-sm sm:text-base text-purple-700 mt-1">Média de Faltas</div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Faltantes */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b border-red-200">
            <h3 className="text-lg font-semibold text-red-800">👥 Alunos que Precisam de Atenção</h3>
            <p className="text-sm text-red-600 mt-1">
              {filteredFaltantes.length} aluno{filteredFaltantes.length !== 1 ? 's' : ''}
              {selectedTurmaFilter ? ` na turma selecionada` : ''}
              {` com ${minFaltas || 1}+ faltas no período`}
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">🔄</div>
              <p className="text-gray-600">Carregando dados de presença...</p>
            </div>
          ) : filteredFaltantes.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-4xl mb-4">🎉</div>
              <p className="text-gray-600 text-lg">Nenhum aluno faltante encontrado!</p>
              <p className="text-gray-500 text-sm mt-2">
                Todos os alunos têm boa frequência no período selecionado.
              </p>
            </div>
          ) : (
            <div>
              {/* Versão Desktop - Tabela */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aluno</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Turma</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Faltas</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Presenças</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">% Presença</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Última Presença</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Contato</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFaltantes.map((aluno, index) => (
                      <tr key={`${aluno.student_id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900">{aluno.student_name}</div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {aluno.turma_nome}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {aluno.faltas}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center text-sm text-gray-600">
                          {aluno.presencas}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            parseFloat(aluno.percentualPresenca) >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {aluno.percentualPresenca}%
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center text-sm text-gray-600">
                          {aluno.ultimaPresenca ?
                            new Date(aluno.ultimaPresenca + 'T00:00:00').toLocaleDateString('pt-BR') :
                            'Nunca'
                          }
                          {aluno.diasSemVir > 0 && (
                            <div className="text-xs text-gray-500">
                              {aluno.diasSemVir} dias atrás
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {aluno.contato ? (
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-sm text-gray-600 hidden lg:inline">{aluno.contato}</span>
                              <a
                                href={gerarLinkWhatsApp(aluno.contato, aluno.student_name, 'faltante')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors duration-200"
                                title="Enviar mensagem pastoral via WhatsApp"
                              >
                                📱 WhatsApp
                              </a>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">Sem contato</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Versão Mobile - Cards */}
              <div className="md:hidden space-y-4">
                {filteredFaltantes.map((aluno, index) => (
                  <div key={`${aluno.student_id}-${index}`} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-lg leading-tight">{aluno.student_name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{aluno.turma_nome}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                          {aluno.faltas} faltas
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">Presenças</p>
                        <p className="text-sm font-medium text-gray-900">{aluno.presencas}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">% Presença</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          parseFloat(aluno.percentualPresenca) >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {aluno.percentualPresenca}%
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">Última Presença</p>
                        <p className="text-sm text-gray-900">
                          {aluno.ultimaPresenca ?
                            new Date(aluno.ultimaPresenca + 'T00:00:00').toLocaleDateString('pt-BR') :
                            'Nunca'
                          }
                        </p>
                        {aluno.diasSemVir > 0 && (
                          <p className="text-xs text-gray-500">{aluno.diasSemVir} dias atrás</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">Contato</p>
                        {aluno.contato ? (
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900">{aluno.contato}</p>
                            <a
                              href={gerarLinkWhatsApp(aluno.contato, aluno.student_name, 'faltante')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium touch-manipulation"
                              title="Enviar mensagem pastoral via WhatsApp"
                            >
                              📱 WhatsApp
                            </a>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">Sem contato</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlunosFaltantes;
